/**
 * Phase 1.5 Milestone D3 — typed adapter for import-row payloads.
 *
 * The 8 row statuses below are the actual runtime states used by the
 * backend (pre-commit set in backend/app/services/import_csv.py
 * validate_rows and post-commit set in backend/app/services/import_worker.py).
 * Never invent statuses. Unknown values render a safe fallback (no
 * resolution actions, no commit-readiness contribution).
 *
 * Pre-commit: VALID, INVALID, DUPLICATE, PENDING
 * Post-commit (terminal): CREATED, UPDATED, SKIPPED, FAILED
 *
 * `resolution` uses the backend VALID_RESOLUTIONS set exactly:
 *   skip | create_anyway | update_existing | manual_review | null
 */

export const ROW_STATUSES = [
  "VALID",
  "INVALID",
  "DUPLICATE",
  "PENDING",
  "CREATED",
  "UPDATED",
  "SKIPPED",
  "FAILED",
] as const;

export const PRE_COMMIT_ROW_STATUSES = ["VALID", "INVALID", "DUPLICATE", "PENDING"] as const;
export const TERMINAL_ROW_STATUSES = ["CREATED", "UPDATED", "SKIPPED", "FAILED"] as const;

export type RowStatus = (typeof ROW_STATUSES)[number];

export function isKnownRowStatus(v: unknown): v is RowStatus {
  return typeof v === "string" && (ROW_STATUSES as readonly string[]).includes(v);
}

export function isTerminalRowStatus(v: unknown): boolean {
  return isKnownRowStatus(v) && (TERMINAL_ROW_STATUSES as readonly string[]).includes(v);
}

export const RESOLUTIONS = ["skip", "create_anyway", "update_existing", "manual_review"] as const;
export type Resolution = (typeof RESOLUTIONS)[number];

export function isKnownResolution(v: unknown): v is Resolution {
  return typeof v === "string" && (RESOLUTIONS as readonly string[]).includes(v);
}

// Server-supported filter values for the /rows endpoint. These are the same
// row statuses; the endpoint upper-cases whatever we send.
export const ROW_STATUS_FILTERS: readonly RowStatus[] = ROW_STATUSES;

// Only the mapped fields we're allowed to display from `row.raw` — these
// mirror import_field_dict.CONTACT_FIELDS. Anything else the server might
// have accidentally stored gets dropped.
const SAFE_RAW_FIELDS = [
  "first_name",
  "last_name",
  "email",
  "phone",
  "location",
  "lifecycle_status",
  "source",
  "priority",
  "tags",
] as const;

export type SafeRawField = (typeof SAFE_RAW_FIELDS)[number];

export const SAFE_RAW_FIELD_LABELS: Record<SafeRawField, string> = {
  first_name: "First Name",
  last_name: "Last Name",
  email: "Email",
  phone: "Phone",
  location: "Location",
  lifecycle_status: "Lifecycle",
  source: "Source",
  priority: "Priority",
  tags: "Tags",
};

export type RowError = { code: string; field?: string };

export type NormalizedRow = {
  id: string;
  rowIndex: number;
  status: RowStatus | null; // null => unknown -> safe fallback
  rawStatus: string; // exact server string, for display when unknown
  values: Partial<Record<SafeRawField, string>>;
  errors: RowError[];
  duplicateMatchIds: string[];
  resolution: Resolution | null;
  targetEntityId: string | null;
  version: number;
  updatedAt: string | null;
};

type RawRow = Record<string, unknown>;

function toStr(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map(toStr).join(", ");
  return "";
}

function normalizeValues(raw: unknown): Partial<Record<SafeRawField, string>> {
  if (!raw || typeof raw !== "object") return {};
  const src = raw as Record<string, unknown>;
  const out: Partial<Record<SafeRawField, string>> = {};
  for (const f of SAFE_RAW_FIELDS) {
    if (f in src) {
      const s = toStr(src[f]);
      if (s) out[f] = s;
    }
  }
  return out;
}

function normalizeErrors(raw: unknown): RowError[] {
  if (!Array.isArray(raw)) return [];
  const out: RowError[] = [];
  for (const e of raw) {
    if (e && typeof e === "object" && typeof (e as RowError).code === "string") {
      const rec = e as RowError;
      out.push({ code: rec.code, ...(rec.field ? { field: rec.field } : {}) });
    }
  }
  return out;
}

/** Normalize one raw row document into the frontend contract. */
export function normalizeRow(raw: RawRow | null | undefined): NormalizedRow | null {
  if (!raw || typeof raw !== "object") return null;
  const id = typeof raw.id === "string" ? raw.id : null;
  if (!id) return null;
  const rowIndex =
    typeof raw.row_index === "number" && Number.isFinite(raw.row_index)
      ? raw.row_index
      : -1;
  const rawStatus = typeof raw.status === "string" ? raw.status : "";
  const status = isKnownRowStatus(rawStatus) ? (rawStatus as RowStatus) : null;
  const resolution =
    typeof raw.resolution === "string" && isKnownResolution(raw.resolution)
      ? (raw.resolution as Resolution)
      : null;
  return {
    id,
    rowIndex,
    status,
    rawStatus,
    values: normalizeValues(raw.raw),
    errors: normalizeErrors(raw.errors),
    duplicateMatchIds: Array.isArray(raw.duplicate_match_ids)
      ? raw.duplicate_match_ids.filter((v): v is string => typeof v === "string")
      : [],
    resolution,
    targetEntityId:
      typeof raw.target_entity_id === "string" ? raw.target_entity_id : null,
    version:
      typeof raw.version === "number" && Number.isFinite(raw.version)
        ? raw.version
        : 0,
    updatedAt: typeof raw.updated_at === "string" ? raw.updated_at : null,
  };
}

export type NormalizedRowsPage = {
  rows: NormalizedRow[];
  total: number;
  page: number;
  limit: number;
};

/** Normalize a GET /rows response envelope. */
export function normalizeRowsPage(env: unknown): NormalizedRowsPage {
  const src = (env ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(src.rows)
    ? (src.rows
        .map((r) => normalizeRow(r as RawRow))
        .filter((r): r is NormalizedRow => !!r))
    : [];
  const total = typeof src.total === "number" ? src.total : 0;
  const page = typeof src.page === "number" ? src.page : 1;
  const limit = typeof src.limit === "number" ? src.limit : 50;
  return { rows, total, page, limit };
}

/**
 * Compute which of the backend's resolutions apply to a given row +
 * duplicate suggested_resolutions list. Never invents; unknown statuses
 * return [].
 */
export function offeredResolutionsFor(
  status: RowStatus | null,
  duplicateSuggested: Resolution[] | null,
): Resolution[] {
  if (!status) return [];
  if (status === "VALID") {
    // Backend: skip | manual_review | create_anyway are the only accepted
    // resolutions for VALID rows.
    return ["skip", "manual_review", "create_anyway"];
  }
  if (status === "INVALID") {
    return ["manual_review"];
  }
  if (status === "DUPLICATE") {
    // The duplicate detector's suggested list IS the offer set. Nothing
    // outside it is server-accepted.
    return (duplicateSuggested || []).filter(isKnownResolution) as Resolution[];
  }
  // PENDING and terminal statuses cannot be resolved.
  return [];
}
